import { useState, useRef, useEffect, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./PostIt.scss";

const PostIt = memo(function PostIt({
  postIt,
  onUpdate,
  onDelete,
  onBringToFront,
}) {
  const { t } = useTranslation();
  const [content, setContent] = useState(postIt.content || "");
  const [color, setColor] = useState(postIt.color || "#f2dd3d");
  const [position, setPosition] = useState(postIt.position || { x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const contentRef = useRef(null);
  // Content last loaded from the server, and its revision - the revision is sent
  // with edits so the server can reject them if the post-it was changed on
  // another device in the meantime
  const loadedContentRef = useRef(postIt.content || "");
  const contentRevRef = useRef(postIt.rev);

  // Show the latest version from the server (e.g. changes made on another
  // device), but keep unsaved typing and the position while dragging
  useEffect(() => {
    if (!isEditing || content === loadedContentRef.current) {
      setContent(postIt.content || "");
      loadedContentRef.current = postIt.content || "";
      contentRevRef.current = postIt.rev;
    }
    if (!isDragging) setPosition(postIt.position || { x: 0, y: 0 });
    setColor(postIt.color || "#f2dd3d");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIt]);

  const colors = [
    { name: t("postIts.colors.yellow"), value: "#f2dd3d" },
    { name: t("postIts.colors.green"), value: "#b4d957" },
    { name: t("postIts.colors.blue"), value: "#5bdee3" },
    { name: t("postIts.colors.pink"), value: "#fab6c2" },
    { name: t("postIts.colors.purple"), value: "#c595cc" },
  ];

  const handleMouseDown = (e) => {
    if (isEditing) return; // Don't drag while editing
    onBringToFront(postIt._id);
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y,
    };
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;

      setPosition({
        x: dragRef.current.initialX + deltaX,
        y: dragRef.current.initialY + deltaY,
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Update position only when dropped
      if (
        position.x !== postIt.position.x ||
        position.y !== postIt.position.y
      ) {
        onUpdate(postIt._id, { position });
      }
    }
  }, [isDragging, position, postIt.position, postIt._id, onUpdate]);

  // Attach global mouse listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleContentClick = () => {
    if (!isDragging) {
      onBringToFront(postIt._id);
      setIsEditing(true);
    }
  };

  const handleContentBlur = () => {
    setIsEditing(false);
    if (content !== postIt.content) {
      onUpdate(postIt._id, { content, rev: contentRevRef.current });
    }
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    onUpdate(postIt._id, { color: newColor });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(postIt._id);
  };

  const handlePinToggle = (e) => {
    e.stopPropagation();
    onUpdate(postIt._id, { isPinned: !postIt.isPinned });
  };

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && contentRef.current) {
      contentRef.current.focus();
      contentRef.current.select();
    }
  }, [isEditing]);

  return (
    <div
      className="postit-wrapper"
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        position: "absolute",
        zIndex: postIt.zIndex || 1,
      }}
    >
      <div
        className="postit"
        style={{
          cursor: isDragging ? "grabbing" : "default",
          "--postit-bg-color": color,
        }}
      >
        <div
          className="header"
          onMouseDown={handleMouseDown}
          style={{ cursor: "grab" }}
        >
          <span
            className="material-symbols-outlined pin-button"
            style={{
              fontSize: "17px",
              color: postIt.isPinned ? "#ff6b6b" : "inherit",
            }}
            onClick={handlePinToggle}
            onMouseDown={(e) => e.stopPropagation()}
          >
            push_pin
          </span>
          <span
            className="material-symbols-outlined delete-button"
            style={{ fontSize: "17px" }}
            onClick={handleDelete}
            onMouseDown={(e) => e.stopPropagation()}
          >
            delete
          </span>
        </div>
        <div
          className="content-area"
          onClick={handleContentClick}
          style={{ cursor: "text" }}
          onMouseDown={(e) => {
            if (isEditing) {
              e.stopPropagation();
            }
          }}
        >
          {isEditing ? (
            <textarea
              ref={contentRef}
              className="content content-textarea"
              value={content}
              onChange={handleContentChange}
              onBlur={handleContentBlur}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ cursor: "text" }}
            />
          ) : (
            <div className="content">{content || t("postIts.clickToEdit")}</div>
          )}
        </div>
        <div
          className="footer"
          onMouseDown={handleMouseDown}
          style={{ cursor: "grab" }}
        >
          {colors.map((c) => (
            <button
              key={c.value}
              className="color-button"
              style={{ backgroundColor: c.value }}
              aria-label={c.name}
              onClick={(e) => {
                e.stopPropagation();
                handleColorChange(c.value);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            ></button>
          ))}
        </div>
      </div>
    </div>
  );
});

export default PostIt;
